// Data-driven schema for the Global Styles accordion. Each OmpSection maps to an ordered list
// of fields; the sidebar renders them generically. Add/tweak a field here, not in the template.
import { OmpStyleVar } from '../order-management-portal.sections';
import { ompPageBackgroundHint } from './omp-image-hints';
import {
  OmpAspectRatio,
  OmpBorderStyle,
  OmpButtonStyle,
  OmpCornerRoundness,
  OmpFieldKind,
  OmpImageControl,
  OmpLogoPosition,
  OmpNavPosition,
  OmpPopupLayout,
  OmpSection,
  OmpStyleField,
} from '../../_models';

const logoPositionOptions = [
  { value: OmpLogoPosition.TopLeft, label: 'Top Left' },
  { value: OmpLogoPosition.TopCenter, label: 'Top Center' },
  { value: OmpLogoPosition.TopRight, label: 'Top Right' },
];

const cornerRoundnessOptions = [
  { value: OmpCornerRoundness.FullyRounded, label: 'Fully Rounded' },
  { value: OmpCornerRoundness.GentlyRounded, label: 'Gently Rounded' },
  { value: OmpCornerRoundness.Square, label: 'Square' },
];

const borderStyleOptions = [
  { value: OmpBorderStyle.Solid, label: 'Solid' },
  { value: OmpBorderStyle.Dashed, label: 'Dashed' },
  { value: OmpBorderStyle.Dotted, label: 'Dotted' },
  { value: OmpBorderStyle.None, label: 'None' },
];

const aspectRatioOptions = [
  { value: OmpAspectRatio.Square, label: 'Square' },
  { value: OmpAspectRatio.Portrait, label: 'Portrait' },
  { value: OmpAspectRatio.Landscape, label: 'Landscape' },
];

const navPositionOptions = [
  { value: OmpNavPosition.Left, label: 'Left' },
  { value: OmpNavPosition.Right, label: 'Right' },
  { value: OmpNavPosition.Top, label: 'Top' },
];

const popupLayoutOptions = [
  { value: OmpPopupLayout.Center, label: 'Center' },
  { value: OmpPopupLayout.Right, label: 'Right' },
];

export const OmpGlobalStyleFields: { [section: string]: OmpStyleField[] } = {
  [OmpSection.PageHeader]: [
    {
      kind: OmpFieldKind.Color,
      key: OmpStyleVar.HeaderBackgroundColor,
      label: 'Header Color',
      help: 'Color behind the logo and navigation.',
    },
    {
      kind: OmpFieldKind.Image,
      key: OmpImageControl.Logo,
      label: 'Logo',
      help: 'Upload a logo image to be used in your pages.',
      hint: 'Recommended File: Svg, Png, Jpg',
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.LogoWidth,
      label: 'Logo Width',
      help: 'Width of the logo in the header.',
      unit: 'px',
      min: 50,
      max: 250,
    },
    {
      kind: OmpFieldKind.Radio,
      key: OmpStyleVar.DesktopLogoPosition,
      label: 'Desktop Logo Position',
      help: 'Where the logo sits on desktop.',
      options: logoPositionOptions,
    },
    {
      kind: OmpFieldKind.Toggle,
      key: OmpStyleVar.ShowHeaderSeparatorLine,
      label: 'Show Separator Line',
      help: 'Draws a line under the header.',
    },
  ],
  [OmpSection.ThemeColors]: [
    {
      kind: OmpFieldKind.Color,
      key: OmpStyleVar.ThemeColor,
      label: 'Brand Color',
      help: 'Used for buttons and other elements',
    },
    {
      kind: OmpFieldKind.Color,
      key: OmpStyleVar.ButtonTextColor,
      label: 'Text In Buttons',
      help: 'Make sure this has enough contrast with your brand color.',
    },
    {
      kind: OmpFieldKind.Color,
      key: OmpStyleVar.ButtonHoverColor,
      label: 'Button Hover Color',
      help: 'Make sure this has enough contrast with your brand color.',
    },
    {
      kind: OmpFieldKind.Color,
      key: OmpStyleVar.LinksAndIconsColor,
      label: 'Links And Icons',
      help: 'Reuse your brand color here. For light brand colors, go darker to enhance contrast.',
    },
    {
      kind: OmpFieldKind.Color,
      key: OmpStyleVar.CardBackgroundColor,
      label: 'Card Background',
      help: 'Most brands leave this white.',
    },
    {
      kind: OmpFieldKind.Color,
      key: OmpStyleVar.CardBorderColor,
      label: 'Card Borders',
      help: 'Give cards borders to help them stand out against the page background',
    },
    {
      kind: OmpFieldKind.Color,
      key: OmpStyleVar.PageBackgroundColor,
      label: 'Page Background',
      help: 'This color should usually be slightly lighter or darker than your card background',
    },
    {
      kind: OmpFieldKind.Image,
      key: OmpImageControl.PageBackground,
      label: 'Background Image',
      help:
        'Image shown behind the portal pages. It fills the whole screen and is cropped to fit, so keep anything ' +
        'important near the centre — a phone shows a tall slice of the middle.',
      hint: ompPageBackgroundHint(),
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.PageBackgroundImageOpacity,
      label: 'Background Opacity',
      help: 'How visible the background image is, or the background color when no image is set.',
      unit: '%',
      min: 0,
      max: 100,
    },
    {
      kind: OmpFieldKind.Color,
      key: OmpStyleVar.BodyTextColor,
      label: 'Text Color',
      help: 'Used for general text make sure this is legible against your background.',
    },
  ],
  [OmpSection.CardStyle]: [
    {
      kind: OmpFieldKind.Radio,
      key: OmpStyleVar.CardCornerRoundness,
      label: 'Corner Roundness',
      help: 'This setting controls how rounded the corners of cards are.',
      options: cornerRoundnessOptions,
    },
    { kind: OmpFieldKind.Subheading, label: 'Border', help: 'Outline drawn around the card.' },
    {
      kind: OmpFieldKind.Radio,
      key: OmpStyleVar.CardBorderStyle,
      label: 'Border Style',
      help: 'Line style of the card border.',
      eye: true,
      options: borderStyleOptions,
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.CardBorderThickness,
      label: 'Border Thickness',
      help: 'Thickness of the card border.',
      unit: 'px',
      min: 0,
      max: 20,
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.CardBorderOpacity,
      label: 'Border Opacity',
      help: 'How visible the card border is.',
      unit: '%',
      min: 0,
      max: 100,
    },
    { kind: OmpFieldKind.Subheading, label: 'Shadow', help: 'Shadow cast behind the card.' },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.CardShadowOpacity,
      label: 'Opacity',
      help: 'How visible the card shadow is.',
      unit: '%',
      min: 0,
      max: 100,
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.CardShadowHorizontalOffset,
      label: 'Horizontal Offset',
      help: 'Shifts the card shadow sideways.',
      unit: 'px',
      min: 0,
      max: 50,
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.CardShadowVerticalOffset,
      label: 'Vertical Offset',
      help: 'Shifts the card shadow down.',
      unit: 'px',
      min: 0,
      max: 50,
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.CardShadowBlur,
      label: 'Blur',
      help: 'Softness of the card shadow.',
      unit: 'px',
      min: 0,
      max: 50,
    },
  ],
  [OmpSection.Images]: [
    {
      kind: OmpFieldKind.Radio,
      key: OmpStyleVar.ProductImageAspectRatio,
      label: 'Product Images Aspect Ratio',
      help: 'This setting controls the aspect ratio of your product images when showcased in groupings such as lists or grids.',
      options: aspectRatioOptions,
    },
    {
      kind: OmpFieldKind.Radio,
      key: OmpStyleVar.ProductImageCornerRoundness,
      label: 'Corner Roundness',
      help: 'This setting controls how rounded the corners of product images are.',
      options: cornerRoundnessOptions,
    },
  ],
  [OmpSection.WidthAndHeight]: [
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.MaxPageWidth,
      label: 'Maximum Page Width',
      help: 'This setting controls how wide full-width elements, like multi-column components and images, can get.',
      unit: 'px',
      min: 800,
      max: 1600,
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.MaxContentWidth,
      label: 'Maximum Content Width',
      help: 'This setting controls how wide single-column content, like products and surveys, can get.',
      unit: 'px',
      min: 600,
      max: 1200,
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.SectionVerticalPadding,
      label: 'Section Vertical Padding',
      help: 'This controls spacing at the top and bottom of the page.',
      unit: 'px',
      min: 0,
      max: 60,
    },
    {
      kind: OmpFieldKind.Slider,
      key: OmpStyleVar.SectionHorizontalPadding,
      label: 'Section Horizontal Padding',
      help: 'This controls spacing between the elements of page sections',
      unit: 'px',
      min: 0,
      max: 60,
    },
  ],
  [OmpSection.Typography]: [
    {
      kind: OmpFieldKind.Dropdown,
      key: OmpStyleVar.HeadingFont,
      label: 'Heading Font',
      help: 'This setting defines the font used for headings.',
    },
    {
      kind: OmpFieldKind.Dropdown,
      key: OmpStyleVar.FontFamily,
      label: 'Body Font',
      help: 'This setting defines the font used for body text.',
    },
  ],
  [OmpSection.Layout]: [
    {
      kind: OmpFieldKind.Toggle,
      key: OmpStyleVar.EnableStickyNavigationalBlocks,
      label: 'Enable Sticky Navigational Blocks',
      help: 'Keeps the navigational blocks on screen while scrolling.',
    },
    {
      kind: OmpFieldKind.Radio,
      key: OmpStyleVar.NavigationBlocksPosition,
      label: 'Navigation Blocks Position',
      help: 'Where the navigational blocks sit on the page.',
      eye: true,
      options: navPositionOptions,
    },
    {
      kind: OmpFieldKind.Radio,
      key: OmpStyleVar.PopupLayout,
      label: 'Pop-Up Layout',
      help: 'Where pop-ups open on the screen.',
      eye: true,
      options: popupLayoutOptions,
    },
    {
      kind: OmpFieldKind.Checkbox,
      label: 'Order Price',
      help: 'Show the order price on the order list page.',
      eye: true,
      checkboxes: [
        { key: OmpStyleVar.OrderPriceOnListPage, label: 'Order List Page' },
      ],
    },
  ],
};

export const OmpButtonShapeFields: OmpStyleField[] = [
  {
    kind: OmpFieldKind.Radio,
    key: OmpStyleVar.ButtonCornerRoundness,
    label: 'Corner Roundness',
    help: 'This setting controls how rounded the corners of buttons are.',
    options: cornerRoundnessOptions,
  },
];

export const OmpButtonStyleFields: OmpStyleField[] = [
  {
    kind: OmpFieldKind.Radio,
    key: 'style',
    label: 'Button Style',
    help: 'Fill and outline of the button.',
    options: [
      { value: OmpButtonStyle.Fill, label: 'Fill' },
      { value: OmpButtonStyle.LowFill, label: 'Low Fill' },
      { value: OmpButtonStyle.Outline, label: 'Out Line' },
      { value: OmpButtonStyle.Text, label: 'Text' },
    ],
  },
  { kind: OmpFieldKind.Subheading, label: 'Border', help: 'Outline drawn around the button.' },
  {
    kind: OmpFieldKind.Slider,
    key: 'border_thickness',
    label: 'Thickness',
    help: 'Thickness of the button border.',
    unit: 'px',
    min: 0,
    max: 20,
  },
  {
    kind: OmpFieldKind.Slider,
    key: 'border_opacity',
    label: 'Opacity',
    help: 'How visible the button border is.',
    unit: '%',
    min: 0,
    max: 100,
  },
  { kind: OmpFieldKind.Subheading, label: 'Shadow', help: 'Shadow cast behind the button.' },
  {
    kind: OmpFieldKind.Slider,
    key: 'shadow_opacity',
    label: 'Opacity',
    help: 'How visible the button shadow is.',
    unit: '%',
    min: 0,
    max: 100,
  },
  {
    kind: OmpFieldKind.Slider,
    key: 'shadow_horizontal_offset',
    label: 'Horizontal Offset',
    help: 'Shifts the button shadow sideways.',
    unit: 'px',
    min: 0,
    max: 50,
  },
  {
    kind: OmpFieldKind.Slider,
    key: 'shadow_vertical_offset',
    label: 'Vertical Offset',
    help: 'Shifts the button shadow down.',
    unit: 'px',
    min: 0,
    max: 50,
  },
  {
    kind: OmpFieldKind.Slider,
    key: 'shadow_blur',
    label: 'Blur',
    help: 'Softness of the button shadow.',
    unit: 'px',
    min: 0,
    max: 50,
  },
];
